import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash-es/debounce';
import kebabCase from 'lodash-es/kebabCase';
import uniqBy from 'lodash-es/uniqBy';

import { customisable_equaliser_icon, icon_close_medium, icon_search } from '@gn-audio/icons';
import {
  Button,
  EcomOnly,
  getTitledHexColorOption,
  isNotNil,
  JabraCheckbox,
  JabraIcon,
  useBreakpoint,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import { Option } from '@gn-audio/jss-core/common/models';

import JabraDropdown from 'common/components/JabraDropdown';
import JabraRangeSlider from 'common/components/JabraRangeSlider';
import { useFormat } from 'common/contexts/FormatContext';
import { ColorFilter } from 'composables/Product/Buy/CustomizerFilters/ColorFilter/ColorFilter';
import {
  formatSfccPriceRange,
  getRefinementValueByAttributeId,
} from 'composables/Product/ProductFilterModule/utils/productFilterModuleUtils';

import { ProductFilterProps, SelectedProductFilters } from './productFilterModuleTypes';

const ProductFilterSidebar: FC<ProductFilterProps> = ({
  searchRefinements,
  options,
  productFilters,
  colorHexMappings,
  setProductFilters,
}) => {
  const [mobileProductFilters, setMobileProductFilters] = useState<SelectedProductFilters>({
    ...productFilters,
  });

  useEffect(() => {
    setMobileProductFilters({ ...productFilters });
  }, [productFilters]);

  const applyFiltersOnMobile = useCallback(() => {
    setProductFilters(mobileProductFilters);
  }, [mobileProductFilters, setProductFilters]);

  const { t } = useFallbackTranslation();
  const { isBelowLg: isMobile } = useBreakpoint('lg', true);
  const { formatCurrency } = useFormat();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<Boolean>(false);
  const [innerSearchText, setInnerSearchText] = useState<string>(productFilters.SearchText || '');

  const searchPlaceholder = options.SearchPlaceholder?.value || '';
  const filters = isMobile ? mobileProductFilters : productFilters;

  const handleDebouncedSearchText = useRef(
    debounce((value) => {
      setProductFilters((prevState) => ({ ...prevState, SearchText: value }));
    }, 250)
  ).current;

  useEffect(() => {
    return () => {
      handleDebouncedSearchText.cancel();
    };
  }, [handleDebouncedSearchText]);

  const handleSearchInputChange = (value: string) => {
    setInnerSearchText(value);
    handleDebouncedSearchText(value);
  };

  const handleSortOption = (option: Option) => {
    setProductFilters((prevState) => ({ ...prevState, Order: option }));
  };

  const sortOptions = options.Orders.map((orderOption) => {
    return {
      key: orderOption.Property.value,
      title: orderOption.Title.value,
      value: `${orderOption.Property.value} ${orderOption.Direction.value}`,
    };
  });

  const handleToggleFilterOption = (
    attributeValue: string,
    filterValue: string,
    isSelected: boolean
  ) => {
    const existGroup = options.FilterGroups.find(
      (group) => group.AttributeName.value === attributeValue
    );
    const selectedGroups = [...filters.FilterGroups];

    if (existGroup) {
      const selectedGroupIndex = selectedGroups.findIndex(
        (group) => group.AttributeName.value === attributeValue
      );
      const existFilter = existGroup.AttachedFilters.find(
        (filter) => filter.Value.value === filterValue
      );

      if (existFilter) {
        if (isSelected) {
          if (selectedGroupIndex !== -1) {
            selectedGroups[selectedGroupIndex] = {
              ...selectedGroups[selectedGroupIndex],
              AttachedFilters: [...selectedGroups[selectedGroupIndex].AttachedFilters, existFilter],
            };
          } else {
            selectedGroups.push({
              ...existGroup,
              AttachedFilters: [existFilter],
            });
          }
        } else if (selectedGroupIndex !== -1) {
          const updatedFilters = selectedGroups[selectedGroupIndex].AttachedFilters.filter(
            (filter) => filter.Value.value !== filterValue
          );

          if (updatedFilters.length === 0) {
            selectedGroups.splice(selectedGroupIndex, 1);
          } else {
            selectedGroups[selectedGroupIndex] = {
              ...selectedGroups[selectedGroupIndex],
              AttachedFilters: updatedFilters,
            };
          }
        }
      }
    }

    if (isMobile) {
      setMobileProductFilters((prevState) => ({
        ...prevState,
        FilterGroups: selectedGroups,
      }));
    } else {
      setProductFilters((prevState) => ({
        ...prevState,
        FilterGroups: selectedGroups,
      }));
    }
  };

  const sfccPriceRange = useMemo(
    () =>
      formatSfccPriceRange(
        getRefinementValueByAttributeId('price', searchRefinements)?.values?.[0]?.value
      ),
    [searchRefinements]
  );

  const sfccColors = useMemo((): Option[] | undefined => {
    const titledColorHexOptions = getRefinementValueByAttributeId(
      'c_color',
      searchRefinements
    )?.values.map((color) =>
      getTitledHexColorOption(color.value, colorHexMappings, color.label)
    ) as Option[];
    return uniqBy(titledColorHexOptions?.filter(isNotNil), 'value');
  }, [searchRefinements, colorHexMappings]);

  const handlePriceRange = (prices: number[]) => {
    const PriceRange = { min: prices[0], max: prices[1] };
    if (isMobile) {
      setMobileProductFilters((prevState) => ({ ...prevState, PriceRange }));
    } else {
      setProductFilters((prevState) => ({ ...prevState, PriceRange }));
    }
  };

  const handleColorChange = (selectedColor: Option) => {
    const updateFilters = (filters: SelectedProductFilters) => {
      const isSelected = filters.SelectedColor.some((color) => color.value === selectedColor.value);
      return {
        ...filters,
        SelectedColor: isSelected
          ? filters.SelectedColor.filter((color) => color.value !== selectedColor.value)
          : [...filters.SelectedColor, selectedColor],
      };
    };

    if (isMobile) {
      setMobileProductFilters(updateFilters);
    } else {
      setProductFilters(updateFilters);
    }
  };

  const minProductPrice = sfccPriceRange.min ?? 0;
  const maxProductPrice = sfccPriceRange.max ?? 1500;
  const priceSlidersValues = [
    Math.max(filters.PriceRange?.min ?? minProductPrice, minProductPrice),
    Math.min(filters.PriceRange?.max ?? maxProductPrice, maxProductPrice),
  ];

  const selectedSorting = filters.Order;

  const selectedColor = filters.SelectedColor;

  const countPriceSliderAsFilter =
    productFilters?.PriceRange &&
    (productFilters.PriceRange?.max !== sfccPriceRange.max ||
      productFilters.PriceRange?.min !== sfccPriceRange.min)
      ? 1
      : 0;

  const countColorPickerAsFilter = productFilters.SelectedColor.length;

  const filtersCount =
    productFilters.FilterGroups.reduce((prev, curr) => {
      return curr.AttachedFilters.length + prev;
    }, 0) +
    countPriceSliderAsFilter +
    countColorPickerAsFilter;

  return (
    <div className="flex flex-col gap-3">
      {isMobile && mobileSidebarOpen && (
        <div className="flex flex-wrap items-center justify-between gap-5 rounded-lg bg-[#f5f5f5] px-4 py-6 lg:mt-4">
          <span id="mobile-sidebar-open-button" className="mr-6">
            {t('system.Filters')}
            {filtersCount > 0 && (
              <span className="text-gray-700">
                &nbsp;({filtersCount}&nbsp;{t('system.Active')})
              </span>
            )}
          </span>
          <Button
            aria-label={`${t('system.Close')} ${t('system.Filters')}`}
            variant="primary"
            size="s"
            icon={<JabraIcon className="p-0.5" Icon={icon_close_medium} />}
            onClick={() => setMobileSidebarOpen(false)}
          />
        </div>
      )}
      <div className="flex flex-col gap-6 rounded-lg bg-[#f5f5f5] px-4 py-6 max-lg:gap-5">
        {(!mobileSidebarOpen || !isMobile) && (
          <div className="relative flex items-center">
            <JabraIcon className="absolute left-3" Icon={icon_search} />
            <input
              className="border-grey h-10 w-full rounded-lg border border-gray-200 py-2 pl-10 pr-4 transition-colors duration-500 hover:border-accent"
              aria-label={t('system.Search')}
              placeholder={searchPlaceholder}
              type="search"
              value={innerSearchText}
              onChange={(e) => handleSearchInputChange(e.target.value)}
            />
          </div>
        )}
        {!isMobile && (
          <div className="relative flex w-full flex-grow items-center self-center">
            <div className="flex min-h-6 flex-wrap items-center p-0 tracking-[0.25px] lg:flex-grow lg:justify-between">
              <span className="mr-1 whitespace-nowrap font-normal">
                {options.OrdersTitle?.value}:
              </span>
              <JabraDropdown
                options={sortOptions}
                selectedOption={selectedSorting}
                onOptionSelect={handleSortOption}
              />
            </div>
          </div>
        )}

        {isMobile && !mobileSidebarOpen && (
          <div className="flex flex-wrap justify-between gap-5 lg:mt-4">
            <div className="flex items-center lg:hidden">
              <Button
                aria-labelledby="mobile-sidebar-open-button"
                variant="primary"
                size="s"
                icon={<JabraIcon className="p-0.5" Icon={customisable_equaliser_icon} />}
                className="mr-6"
                onClick={() => setMobileSidebarOpen(true)}
              />

              <span id="mobile-sidebar-open-button" className="mr-6">
                {t('system.Filters')}
              </span>
            </div>

            <div className="sm:basis-1/3lg:ml-0 relative flex items-center self-center lg:flex-grow">
              <div className="flex min-h-6 flex-wrap items-center p-0 tracking-[0.25px] lg:flex-grow lg:justify-between">
                <span className="mr-1 whitespace-nowrap font-normal">
                  {options.OrdersTitle?.value}:
                </span>
                <JabraDropdown
                  options={sortOptions}
                  selectedOption={selectedSorting}
                  onOptionSelect={handleSortOption}
                />
              </div>
            </div>
          </div>
        )}

        {(mobileSidebarOpen || !isMobile) && (
          <>
            {options.FilterGroups.map((filterGroup) => {
              // Find the corresponding selected group in SelectedProductFilters
              const selectedGroup = filters.FilterGroups.find(
                (group) => group.AttributeName.value === filterGroup.AttributeName.value
              );

              return (
                <div key={filterGroup.Title.value}>
                  <span className="heading-6">{filterGroup.Title.value}</span>
                  <ul className="mt-3 flex flex-col gap-3">
                    {filterGroup.AttachedFilters.map((filter) => {
                      // Check if the filter is selected based on SelectedProductFilters
                      const isSelected = selectedGroup?.AttachedFilters.some(
                        (selectedFilter) => selectedFilter.Value.value === filter.Value.value
                      );

                      return (
                        <li key={filter.Title.value}>
                          <label className="flex cursor-pointer items-center gap-2">
                            <JabraCheckbox
                              className="flex-none"
                              aria-labelledby={`${kebabCase(filter.Title.value)}-checkbox-label`}
                              checked={isSelected || false}
                              onCheckedChange={() =>
                                handleToggleFilterOption(
                                  filterGroup.AttributeName.value,
                                  filter.Value.value,
                                  !isSelected
                                )
                              }
                            />
                            <span
                              className="flex-1 break-words break-all leading-tight"
                              id={`${kebabCase(filter.Title.value)}-checkbox-label`}
                            >
                              {filter.Title.value}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}

            {options.PriceFilterOptionEnabled.value && (
              <EcomOnly>
                <div>
                  <span className="heading-6">{options.PriceFilterOptionTitle?.value}</span>
                  <div className="mt-4 flex w-full flex-col items-center">
                    <JabraRangeSlider
                      values={priceSlidersValues}
                      onValueChange={(prices) => handlePriceRange(prices)}
                      min={sfccPriceRange.min}
                      max={sfccPriceRange.max}
                      labelFormatter={(value) => formatCurrency(value, 0)}
                      step={10}
                    />
                  </div>
                </div>
              </EcomOnly>
            )}

            {options.ColorsFilterOptionEnabled.value && sfccColors && (
              <EcomOnly>
                <div>
                  <span className="heading-6">{options.ColorsFilterOptionTitle?.value}</span>
                  <ColorFilter
                    options={sfccColors}
                    selected={selectedColor}
                    onSelect={handleColorChange}
                    showColorTitle={false}
                    containerClassName="mt-4 flex w-full flex-wrap justify-start items-center"
                  />
                </div>
              </EcomOnly>
            )}
          </>
        )}
      </div>
      {mobileSidebarOpen && isMobile && (
        <div className="mt-[0.625rem] flex justify-center gap-4 border-t border-gray-90 p-4">
          <Button
            variant="tertiary"
            size="m"
            onClick={() => setProductFilters({ FilterGroups: [], SelectedColor: [] })}
            disabled={filtersCount === 0}
          >
            {t('system.Clear')}
            {filtersCount > 0 && ` (${filtersCount})`}
          </Button>
          <Button variant="primary" size="m" onClick={() => applyFiltersOnMobile()}>
            {t('cart.Apply')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductFilterSidebar;
